import { useCallback, useEffect, useState } from 'react';
import adminApiClient from '@/services/adminApiClient';
import type {
  ContentFlagItem,
  ContentStats,
  ExamCatalogEntry,
  ExamPattern,
  ExamSessionDetail,
  ExamSessionItem,
  PracticeSessionItem,
  TopicTaxonomyItem,
  TopTopicItem,
} from '@/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDatetime(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatCard({ label, value, accent, sub }: { label: string; value: number; accent?: string; sub?: string }) {
  return (
    <div className="bg-white border border-admin-border rounded-xl p-5">
      <p className="text-sm text-slate-500">{label}</p>
      <p className={`text-3xl font-semibold mt-1 ${accent ?? 'text-slate-800'}`}>{value.toLocaleString()}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

const PATTERN_STATUS_COLORS: Record<string, string> = {
  approved:       'bg-green-100 text-green-700',
  pending_review: 'bg-yellow-100 text-yellow-700',
  rejected:       'bg-red-100 text-red-600',
};

const FLAG_STATUS_COLORS: Record<string, string> = {
  pending:   'bg-yellow-100 text-yellow-700',
  resolved:  'bg-green-100 text-green-700',
  dismissed: 'bg-slate-100 text-slate-500',
};

function Badge({ label, colorClass }: { label: string; colorClass: string }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>{label}</span>
  );
}

function JsonViewer({ data, title }: { data: any; title: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-indigo-600 hover:underline text-xs font-medium">
        View JSON
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center px-5 py-4 border-b border-admin-border">
              <h3 className="font-semibold text-slate-800">{title}</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 text-lg">✕</button>
            </div>
            <pre className="overflow-auto p-5 text-xs text-slate-700 font-mono flex-1">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab: Overview
// ---------------------------------------------------------------------------

function OverviewTab() {
  const [stats, setStats] = useState<ContentStats | null>(null);
  const [topics, setTopics] = useState<TopTopicItem[]>([]);
  const [sessions, setSessions] = useState<ExamSessionItem[]>([]);
  const [sessDetail, setSessDetail] = useState<ExamSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminApiClient.get('/content/stats'),
      adminApiClient.get('/content/top-topics?limit=15'),
      adminApiClient.get('/content/exam-sessions?page_size=10'),
    ]).then(([s, t, es]) => {
      setStats(s.data);
      setTopics(t.data);
      setSessions(es.data.items);
    }).finally(() => setLoading(false));
  }, []);

  async function openSession(id: string) {
    const r = await adminApiClient.get(`/content/exam-sessions/${id}`);
    setSessDetail(r.data);
  }

  if (loading) return <p className="text-slate-400 text-sm py-10 text-center">Loading…</p>;
  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Exam Patterns" value={stats.exam_patterns} sub={`${stats.patterns_pending_review} pending review`} accent={stats.patterns_pending_review > 0 ? 'text-yellow-600' : undefined} />
        <StatCard label="Exam Sessions" value={stats.exam_sessions} />
        <StatCard label="Practice Sessions" value={stats.practice_sessions} />
        <StatCard label="Pending Flags" value={stats.pending_flags} accent={stats.pending_flags > 0 ? 'text-red-600' : undefined} />
        <StatCard label="Catalog Exams" value={stats.catalog_exams} />
        <StatCard label="Taxonomy Topics" value={stats.topics} />
      </div>

      {/* Top Topics */}
      {topics.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Top Topics by Learners</h3>
          <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Topic</th>
                  <th className="text-right px-4 py-3">Learners</th>
                  <th className="text-right px-4 py-3">Avg Mastery</th>
                </tr>
              </thead>
              <tbody>
                {topics.map((t, i) => (
                  <tr key={t.topic} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-slate-400 text-xs">{i + 1}</td>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{t.topic}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{t.learner_count}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${t.avg_mastery * 100}%` }} />
                        </div>
                        <span className="text-slate-600 text-xs w-10 text-right">{(t.avg_mastery * 100).toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Exam Sessions */}
      {sessions.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Recent Exam Sessions</h3>
          <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Exam</th>
                  <th className="text-left px-4 py-3">Paper / Mode</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Score</th>
                  <th className="text-left px-4 py-3">Started</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-800">{s.user_name ?? s.user_email}</div>
                      {s.user_name && <div className="text-xs text-slate-400">{s.user_email}</div>}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-700">{s.exam_name}</td>
                    <td className="px-4 py-2.5 text-slate-500">{s.paper_type} / {s.mode}</td>
                    <td className="px-4 py-2.5">
                      <Badge label={s.status} colorClass={s.status === 'submitted' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'} />
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{s.score != null ? s.score : '—'}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{fmt(s.started_at)}</td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => openSession(s.id)} className="text-indigo-600 hover:underline text-xs font-medium">Detail</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Session Detail Modal */}
      {sessDetail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center px-5 py-4 border-b border-admin-border">
              <div>
                <h3 className="font-semibold text-slate-800">{sessDetail.exam_name}</h3>
                <p className="text-xs text-slate-400">{sessDetail.user_email} · {sessDetail.paper_type} · {sessDetail.mode}</p>
              </div>
              <button onClick={() => setSessDetail(null)} className="text-slate-400 hover:text-slate-600 text-lg">✕</button>
            </div>
            <div className="overflow-y-auto p-5 space-y-4 flex-1">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Status</p>
                  <p className="font-medium text-slate-700">{sessDetail.status}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Score</p>
                  <p className="font-medium text-slate-700">{sessDetail.score ?? '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Submitted</p>
                  <p className="font-medium text-slate-700">{fmt(sessDetail.submitted_at)}</p>
                </div>
              </div>
              {sessDetail.results && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Results</p>
                  <JsonViewer data={sessDetail.results} title="Results" />
                </div>
              )}
              {sessDetail.analysis && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Analysis</p>
                  <JsonViewer data={sessDetail.analysis} title="Analysis" />
                </div>
              )}
              {sessDetail.paper_data && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Paper Data</p>
                  <JsonViewer data={sessDetail.paper_data} title="Paper Data" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Exam Patterns
// ---------------------------------------------------------------------------

function ExamPatternsTab() {
  const [items, setItems] = useState<ExamPattern[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExamPattern | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApiClient.get('/content/exam-patterns', {
      params: { page, page_size: PAGE_SIZE, search, status: statusFilter },
    }).then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function setStatus(id: string, status: string) {
    await adminApiClient.patch(`/content/exam-patterns/${id}/status`, { status });
    load();
  }

  async function doDelete(id: string) {
    await adminApiClient.delete(`/content/exam-patterns/${id}`);
    setDeleteTarget(null);
    load();
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          className="border border-admin-border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder="Search exam_id or paper type…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          <option value="pending_review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <span className="ml-auto text-sm text-slate-500 self-center">{total} pattern(s)</span>
      </div>

      <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3">Exam ID</th>
              <th className="text-left px-4 py-3">Paper Type</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-left px-4 py-3">Pattern</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-10">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-10">
                No patterns cached yet — they appear after students first trigger an exam.
              </td></tr>
            ) : items.map(p => (
              <tr key={p.id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono font-medium text-slate-800">{p.exam_id}</td>
                <td className="px-4 py-3 text-slate-600">{p.paper_type}</td>
                <td className="px-4 py-3 text-slate-500">{p.source}</td>
                <td className="px-4 py-3">
                  <Badge label={p.status.replace('_', ' ')} colorClass={PATTERN_STATUS_COLORS[p.status] ?? 'bg-slate-100 text-slate-600'} />
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{fmt(p.created_at)}</td>
                <td className="px-4 py-3">
                  <JsonViewer data={p.pattern_data} title={`${p.exam_id} – ${p.paper_type}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 justify-end">
                    {p.status !== 'approved' && (
                      <button onClick={() => setStatus(p.id, 'approved')} className="text-green-600 hover:underline text-xs font-medium">Approve</button>
                    )}
                    {p.status !== 'rejected' && (
                      <button onClick={() => setStatus(p.id, 'rejected')} className="text-orange-500 hover:underline text-xs font-medium">Reject</button>
                    )}
                    {p.status !== 'pending_review' && (
                      <button onClick={() => setStatus(p.id, 'pending_review')} className="text-slate-400 hover:underline text-xs">Reset</button>
                    )}
                    <button onClick={() => setDeleteTarget(p)} className="text-red-500 hover:underline text-xs font-medium">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50">← Prev</button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50">Next →</button>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Delete Pattern?</h2>
            <p className="text-sm text-slate-500 mb-2">
              <span className="font-mono font-semibold text-slate-700">{deleteTarget.exam_id}</span> — {deleteTarget.paper_type}
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5">
              The pattern will be regenerated from the LLM the next time a student requests this exam.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={() => doDelete(deleteTarget.id)} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Exam Catalog
// ---------------------------------------------------------------------------

function ExamCatalogTab() {
  const [items, setItems] = useState<ExamCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<ExamCatalogEntry | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ExamCatalogEntry | null>(null);

  const [form, setForm] = useState({ exam_id: '', display_name: '', paper_types_raw: '', is_active: true });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const load = useCallback(() => {
    adminApiClient.get('/content/exam-catalog')
      .then(r => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditTarget(null);
    setForm({ exam_id: '', display_name: '', paper_types_raw: '', is_active: true });
    setFormError('');
    setShowForm(true);
  }

  function openEdit(e: ExamCatalogEntry) {
    setEditTarget(e);
    setForm({ exam_id: e.exam_id, display_name: e.display_name, paper_types_raw: e.paper_types.join(', '), is_active: e.is_active });
    setFormError('');
    setShowForm(true);
  }

  async function submitForm() {
    setFormError('');
    if (!form.exam_id.trim() && !editTarget) { setFormError('Exam ID is required'); return; }
    if (!form.display_name.trim()) { setFormError('Display name is required'); return; }
    const paper_types = form.paper_types_raw.split(',').map(s => s.trim()).filter(Boolean);
    setFormLoading(true);
    try {
      if (editTarget) {
        await adminApiClient.patch(`/content/exam-catalog/${editTarget.id}`, {
          display_name: form.display_name.trim(),
          paper_types,
          is_active: form.is_active,
        });
      } else {
        await adminApiClient.post('/content/exam-catalog', {
          exam_id: form.exam_id.trim(),
          display_name: form.display_name.trim(),
          paper_types,
          is_active: form.is_active,
        });
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      setFormError(e.response?.data?.detail ?? 'Failed to save');
    } finally {
      setFormLoading(false);
    }
  }

  async function doDelete(id: string) {
    await adminApiClient.delete(`/content/exam-catalog/${id}`);
    setDeleteTarget(null);
    load();
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={openCreate} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">+ Add Exam</button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm text-center py-10">Loading…</p>
      ) : (
        <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-3">Exam ID</th>
                <th className="text-left px-4 py-3">Display Name</th>
                <th className="text-left px-4 py-3">Paper Types</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-slate-400 py-10">No exams registered yet</td></tr>
              ) : items.map(e => (
                <tr key={e.id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono font-semibold text-slate-800">{e.exam_id}</td>
                  <td className="px-4 py-3 text-slate-700">{e.display_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {e.paper_types.length === 0
                        ? <span className="text-slate-400 text-xs">None</span>
                        : e.paper_types.map(pt => (
                          <span key={pt} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{pt}</span>
                        ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge label={e.is_active ? 'Active' : 'Inactive'} colorClass={e.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'} />
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmt(e.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 justify-end">
                      <button onClick={() => openEdit(e)} className="text-indigo-600 hover:underline text-xs font-medium">Edit</button>
                      <button onClick={() => setDeleteTarget(e)} className="text-red-500 hover:underline text-xs font-medium">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-5">{editTarget ? 'Edit Exam' : 'Add Exam'}</h2>
            <div className="space-y-4">
              {!editTarget && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Exam ID <span className="text-red-500">*</span> <span className="text-slate-400 font-normal text-xs">(e.g. JEE_MAIN)</span></label>
                  <input className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none"
                    value={form.exam_id} onChange={e => setForm(f => ({ ...f, exam_id: e.target.value.toUpperCase() }))} />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Display Name <span className="text-red-500">*</span></label>
                <input className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  placeholder="JEE Main" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Paper Types <span className="text-slate-400 font-normal">(comma-separated)</span></label>
                <input className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  placeholder="Physics, Chemistry, Mathematics" value={form.paper_types_raw}
                  onChange={e => setForm(f => ({ ...f, paper_types_raw: e.target.value }))} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="catalog-active" checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                <label htmlFor="catalog-active" className="text-sm text-slate-700">Active (visible to students)</label>
              </div>
            </div>
            {formError && <p className="text-red-500 text-sm mt-3">{formError}</p>}
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={submitForm} disabled={formLoading} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {formLoading ? 'Saving…' : editTarget ? 'Save Changes' : 'Add Exam'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Remove Exam?</h2>
            <p className="text-sm text-slate-500 mb-5">Remove <span className="font-semibold text-slate-700">{deleteTarget.display_name}</span> from the catalog?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={() => doDelete(deleteTarget.id)} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Content Flags
// ---------------------------------------------------------------------------

function ContentFlagsTab() {
  const [items, setItems] = useState<ContentFlagItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<ContentFlagItem | null>(null);
  const [resolveAction, setResolveAction] = useState<'resolved' | 'dismissed'>('resolved');
  const [resolveNote, setResolveNote] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminApiClient.get('/content/flags', { params: { page, page_size: PAGE_SIZE, status: statusFilter, search } })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [page, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  async function submitResolve() {
    if (!resolveTarget) return;
    setResolveLoading(true);
    try {
      await adminApiClient.patch(`/content/flags/${resolveTarget.id}/resolve`, {
        action: resolveAction,
        resolution_note: resolveNote || undefined,
      });
      setResolveTarget(null);
      load();
    } finally {
      setResolveLoading(false);
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input className="border border-admin-border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder="Search user or reason…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
          value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <span className="ml-auto text-sm text-slate-500 self-center">{total} flag(s)</span>
      </div>

      <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Feature</th>
              <th className="text-left px-4 py-3">Reason</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Flagged</th>
              <th className="text-left px-4 py-3">Snapshot</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-10">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-10">No flags found</td></tr>
            ) : items.map(f => (
              <tr key={f.id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{f.user_name ?? f.user_email}</div>
                  {f.user_name && <div className="text-xs text-slate-400">{f.user_email}</div>}
                </td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{f.feature}</span></td>
                <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{f.reason}</td>
                <td className="px-4 py-3">
                  <Badge label={f.status} colorClass={FLAG_STATUS_COLORS[f.status] ?? 'bg-slate-100 text-slate-600'} />
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{fmtDatetime(f.created_at)}</td>
                <td className="px-4 py-3">
                  {f.content_snapshot
                    ? <JsonViewer data={f.content_snapshot} title="Content Snapshot" />
                    : <span className="text-slate-400 text-xs">—</span>}
                </td>
                <td className="px-4 py-3">
                  {f.status === 'pending' && (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => { setResolveTarget(f); setResolveAction('resolved'); setResolveNote(''); }}
                        className="text-green-600 hover:underline text-xs font-medium">Resolve</button>
                      <button onClick={() => { setResolveTarget(f); setResolveAction('dismissed'); setResolveNote(''); }}
                        className="text-slate-400 hover:underline text-xs">Dismiss</button>
                    </div>
                  )}
                  {f.status !== 'pending' && f.resolved_by && (
                    <span className="text-xs text-slate-400">by {f.resolved_by}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50">← Prev</button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50">Next →</button>
        </div>
      )}

      {resolveTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Review Flag</h2>
            <p className="text-sm text-slate-600 mb-1"><span className="font-medium">User:</span> {resolveTarget.user_email}</p>
            <p className="text-sm text-slate-600 mb-1"><span className="font-medium">Feature:</span> {resolveTarget.feature}</p>
            <p className="text-sm text-slate-600 mb-4"><span className="font-medium">Reason:</span> {resolveTarget.reason}</p>

            <div className="flex gap-3 mb-4">
              {(['resolved', 'dismissed'] as const).map(a => (
                <button key={a} onClick={() => setResolveAction(a)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${resolveAction === a ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-admin-border hover:border-indigo-300'}`}>
                  {a.charAt(0).toUpperCase() + a.slice(1)}
                </button>
              ))}
            </div>

            <label className="block text-sm font-medium text-slate-700 mb-1">Note (optional)</label>
            <input className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none"
              placeholder="e.g. Confirmed hallucination, pattern deleted" value={resolveNote}
              onChange={e => setResolveNote(e.target.value)} />

            <div className="flex gap-3 justify-end">
              <button onClick={() => setResolveTarget(null)} className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={submitResolve} disabled={resolveLoading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {resolveLoading ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Topics
// ---------------------------------------------------------------------------

function TopicsTab() {
  const [items, setItems] = useState<TopicTaxonomyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<TopicTaxonomyItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TopicTaxonomyItem | null>(null);
  const [form, setForm] = useState({ slug: '', display_name: '', subject_area: '', description: '', is_active: true });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const load = useCallback(() => {
    adminApiClient.get('/content/topics', { params: { search, subject_area: areaFilter } })
      .then(r => setItems(r.data))
      .finally(() => setLoading(false));
  }, [search, areaFilter]);

  useEffect(() => { load(); }, [load]);

  const subjectAreas = [...new Set(items.map(t => t.subject_area).filter(Boolean))] as string[];

  function openCreate() {
    setEditTarget(null);
    setForm({ slug: '', display_name: '', subject_area: '', description: '', is_active: true });
    setFormError('');
    setShowForm(true);
  }

  function openEdit(t: TopicTaxonomyItem) {
    setEditTarget(t);
    setForm({ slug: t.slug, display_name: t.display_name, subject_area: t.subject_area ?? '', description: t.description ?? '', is_active: t.is_active });
    setFormError('');
    setShowForm(true);
  }

  async function submitForm() {
    setFormError('');
    if (!editTarget && !form.slug.trim()) { setFormError('Slug is required'); return; }
    if (!form.display_name.trim()) { setFormError('Display name is required'); return; }
    setFormLoading(true);
    try {
      if (editTarget) {
        await adminApiClient.patch(`/content/topics/${editTarget.id}`, {
          display_name: form.display_name.trim(),
          subject_area: form.subject_area.trim() || null,
          description:  form.description.trim() || null,
          is_active:    form.is_active,
        });
      } else {
        await adminApiClient.post('/content/topics', {
          slug:         form.slug.trim().toLowerCase(),
          display_name: form.display_name.trim(),
          subject_area: form.subject_area.trim() || null,
          description:  form.description.trim() || null,
          is_active:    form.is_active,
        });
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      setFormError(e.response?.data?.detail ?? 'Failed to save');
    } finally {
      setFormLoading(false);
    }
  }

  async function doDelete(id: string) {
    await adminApiClient.delete(`/content/topics/${id}`);
    setDeleteTarget(null);
    load();
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input className="border border-admin-border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder="Search topics…" value={search}
          onChange={e => setSearch(e.target.value)} />
        {subjectAreas.length > 0 && (
          <select className="border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
            value={areaFilter} onChange={e => setAreaFilter(e.target.value)}>
            <option value="">All subjects</option>
            {subjectAreas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <span className="ml-auto text-sm text-slate-500 self-center">{items.length} topic(s)</span>
        <button onClick={openCreate} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">+ Add Topic</button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm text-center py-10">Loading…</p>
      ) : (
        <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-3">Slug</th>
                <th className="text-left px-4 py-3">Display Name</th>
                <th className="text-left px-4 py-3">Subject Area</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-slate-400 py-10">No topics defined yet</td></tr>
              ) : items.map(t => (
                <tr key={t.id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-600 text-xs">{t.slug}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{t.display_name}</td>
                  <td className="px-4 py-3 text-slate-500">{t.subject_area ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge label={t.is_active ? 'Active' : 'Inactive'} colorClass={t.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 justify-end">
                      <button onClick={() => openEdit(t)} className="text-indigo-600 hover:underline text-xs font-medium">Edit</button>
                      <button onClick={() => setDeleteTarget(t)} className="text-red-500 hover:underline text-xs font-medium">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-5">{editTarget ? 'Edit Topic' : 'Add Topic'}</h2>
            <div className="space-y-4">
              {!editTarget && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Slug <span className="text-red-500">*</span> <span className="text-slate-400 font-normal text-xs">(lowercase, e.g. calculus)</span></label>
                  <input className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none"
                    value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase() }))} />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Display Name <span className="text-red-500">*</span></label>
                <input className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject Area</label>
                <input className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  placeholder="e.g. Mathematics, Physics" value={form.subject_area}
                  onChange={e => setForm(f => ({ ...f, subject_area: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="topic-active" checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                <label htmlFor="topic-active" className="text-sm text-slate-700">Active</label>
              </div>
            </div>
            {formError && <p className="text-red-500 text-sm mt-3">{formError}</p>}
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={submitForm} disabled={formLoading} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {formLoading ? 'Saving…' : editTarget ? 'Save' : 'Add Topic'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Delete Topic?</h2>
            <p className="text-sm text-slate-500 mb-5">Remove <span className="font-semibold text-slate-700">{deleteTarget.display_name}</span> from the taxonomy?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={() => doDelete(deleteTarget.id)} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Practice Sessions
// ---------------------------------------------------------------------------

function PracticeSessionsTab() {
  const [items, setItems] = useState<PracticeSessionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminApiClient.get('/content/practice-sessions', { params: { page, page_size: PAGE_SIZE, search } })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <input className="border border-admin-border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder="Search user or topic…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <span className="ml-auto text-sm text-slate-500 self-center">{total} session(s)</span>
      </div>

      <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Topic</th>
              <th className="text-left px-4 py-3">Goal</th>
              <th className="text-left px-4 py-3">Score</th>
              <th className="text-left px-4 py-3">Verdict</th>
              <th className="text-left px-4 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center text-slate-400 py-10">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-slate-400 py-10">No practice sessions yet</td></tr>
            ) : items.map(s => (
              <tr key={s.id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{s.user_name ?? s.user_email}</div>
                  {s.user_name && <div className="text-xs text-slate-400">{s.user_email}</div>}
                </td>
                <td className="px-4 py-3 text-slate-700">{s.topic}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{s.goal}</td>
                <td className="px-4 py-3">
                  {s.overall_score != null ? (
                    <span className={`font-semibold ${s.overall_score >= 70 ? 'text-green-600' : s.overall_score >= 40 ? 'text-yellow-600' : 'text-red-500'}`}>
                      {s.overall_score}%
                    </span>
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{s.verdict ?? '—'}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{fmt(s.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50">← Prev</button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50">Next →</button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'patterns',   label: 'Exam Patterns' },
  { id: 'catalog',    label: 'Exam Catalog' },
  { id: 'flags',      label: 'Content Flags' },
  { id: 'topics',     label: 'Topics' },
  { id: 'practice',   label: 'Practice Sessions' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function Content() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-800 mb-6">Content &amp; Exams</h1>

      <div className="flex gap-1 border-b border-admin-border mb-6 overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview'  && <OverviewTab />}
      {activeTab === 'patterns'  && <ExamPatternsTab />}
      {activeTab === 'catalog'   && <ExamCatalogTab />}
      {activeTab === 'flags'     && <ContentFlagsTab />}
      {activeTab === 'topics'    && <TopicsTab />}
      {activeTab === 'practice'  && <PracticeSessionsTab />}
    </div>
  );
}
